// src/pages/Auditorias/Detalhe/index.js
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { ehAdmin } from '../../../utils/permissoes';
import { useAuditoriaDetalhe } from '../hooks/useAuditoriaDetalhe';
import SeccaoColapsavel from '../components/SeccaoColapsavel';
import ProblemasLista from '../components/ProblemasLista';
import FicheirosArvore from '../components/FicheirosArvore';
import HistoricoLista from '../components/HistoricoLista';
import ParecerDialog from '../components/ParecerDialog';
import ConteudoDialog from '../components/ConteudoDialog';
import { descarregarZip } from '../api';
import {
  ESTADO_AUDITORIA_LABEL, formatarRelativo,
  descreverProgresso, percentagemProgresso
} from '../utils/formatacao';
import '../Auditorias.css';

const Detalhe = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const admin = ehAdmin(user);
  const {
    auditoria, problemas, ficheiros, carregando, erro, emCurso,
    carregarProblemas, carregarFicheiros, validar, relancar,
    cancelar, anotar, actualizarTudo
  } = useAuditoriaDetalhe(id);

  const [parecerAberto, setParecerAberto] = useState(false);
  const [alvo, setAlvo] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [zipEmCurso, setZipEmCurso] = useState(null);

  // O ZIP é montado no servidor a partir da BD; num processo com centenas de
  // transcrições demora uns segundos, daí o estado no botão.
  const baixarZip = async (tipo) => {
    setAviso(null);
    setZipEmCurso(tipo);
    try {
      const sufixo = tipo === 'parecer' ? 'Parecer' : 'Processo';
      await descarregarZip(id, tipo, `${auditoria.pasta}-${sufixo}.zip`);
    } catch (e) {
      setAviso(e.mensagemLegivel || e.response?.data?.message || 'Erro ao gerar o ZIP');
    } finally {
      setZipEmCurso(null);
    }
  };

  const pararAuditoria = async () => {
    if (!window.confirm(
      'Parar esta auditoria?\n\n'
      + 'O que já foi transcrito e avaliado mantém-se acessível, e depois pode '
      + 'retomar de onde parou com o botão RETOMAR.'
    )) return;

    setAviso(null);
    try {
      const resposta = await cancelar();
      setAviso(resposta.message || 'Auditoria parada.');
    } catch (e) {
      setAviso(e.response?.data?.message || 'Erro ao parar a auditoria');
    }
  };

  const relancarModo = async (modo) => {
    setAviso(null);
    try {
      await relancar(modo);
      setAviso(modo === 'atualizacao'
        ? 'Atualização enfileirada — só entram movimentos novos.'
        : 'Retoma enfileirada — continua de onde parou.');
    } catch (e) {
      setAviso(
        e.response?.status === 409
          ? 'Já existe uma execução na fila ou a decorrer.'
          : e.response?.data?.message || 'Erro ao enfileirar'
      );
    }
  };

  if (carregando) return <div className="aud-pagina"><p className="aud-vazio">A carregar…</p></div>;
  if (erro) return <div className="aud-pagina"><p className="aud-erro">{erro}</p></div>;
  if (!auditoria) return null;

  const c = auditoria.contagens || {};
  const exec = auditoria.execucao_corrente;

  // Modo carimbado no mestre na criação — não muda. Ausente vale como profundo,
  // que é o comportamento de todas as auditorias anteriores à Etapa 10.
  const modoUnico = auditoria.avaliar_peca_individual === false;

  // O descritor de progresso junta a etapa (da execução) aos contadores
  // derivados das peças (das contagens) — vêm de sítios diferentes
  const fonteProgresso = {
    ...c,
    avaliar_peca_individual: auditoria.avaliar_peca_individual,
    etapa_atual: exec?.etapa_atual,
    fila_estado: auditoria.fila?.estado,
    fila_posicao: auditoria.fila?.posicao,
    ...(auditoria.ultima_peca
      ? { ultima_peca: auditoria.ultima_peca.nome_original,
          ultima_peca_estado: auditoria.ultima_peca.estado }
      : {}),
  };
  const progresso = emCurso ? descreverProgresso(fonteProgresso) : null;
  const percentagem = emCurso ? percentagemProgresso(fonteProgresso) : null;

  return (
    <div className="aud-pagina">
      <Link to="/auditorias" className="aud-link">‹ voltar às auditorias</Link>

      <div className="aud-cabecalho">
        <div>
          <h1>{auditoria.nome_cliente || `Cliente ${auditoria.cod_cli}`}</h1>
          <p className="aud-sub">
            <span className="aud-mono">{auditoria.cod_processo}</span>
            {auditoria.num_processo && ` · ${auditoria.num_processo}`}
            {` · ${auditoria.user_email}`}
          </p>
        </div>

        <div className="aud-cabecalho-accoes">
          <button type="button" className="aud-btn-secundario" onClick={actualizarTudo}>
            ⟳ ACTUALIZAR
          </button>
          <button
            type="button"
            className="aud-btn-primario"
            disabled={!auditoria.parecer_id}
            title={auditoria.parecer_id ? 'Ver parecer final' : 'Parecer por emitir'}
            onClick={() => setParecerAberto(true)}
          >
            PARECER
          </button>
          {emCurso ? (
            <button type="button" className="aud-btn-secundario" onClick={pararAuditoria}>
              PARAR
            </button>
          ) : (
            <>
              <button
                type="button"
                className="aud-btn-secundario"
                onClick={() => relancarModo('continuar')}
                title="Retoma as peças que ficaram por processar"
              >
                RETOMAR
              </button>
              <button
                type="button"
                className="aud-btn-secundario"
                onClick={() => relancarModo('atualizacao')}
                title="Procura movimentos novos na DuraLex"
              >
                PROCURAR NOVOS
              </button>
            </>
          )}
        </div>
      </div>

      <div className="aud-estado-linha">
        <span className={`aud-badge aud-estado-${auditoria.estado}`}>
          {ESTADO_AUDITORIA_LABEL[auditoria.estado] || auditoria.estado}
        </span>
        {!emCurso && (
          <span className="aud-sub">
            última atualização {formatarRelativo(auditoria.ultima_atualizacao)}
          </span>
        )}
      </div>

      {emCurso && progresso && (
        <div className="aud-painel-progresso">
          <div className="aud-painel-progresso-topo">
            {progresso.passo && <span className="aud-passo">{progresso.passo}</span>}
            <span className="aud-progresso-vivo">{progresso.etapa}</span>
            {progresso.contador && <span className="aud-sub">{progresso.contador}</span>}
            {percentagem !== null && <span className="aud-sub">{percentagem}%</span>}
          </div>
          {percentagem !== null && (
            <div className="aud-barra">
              <div className="aud-barra-cheia" style={{ width: `${percentagem}%` }} />
            </div>
          )}
          {progresso.pormenor && (
            <div className="aud-sub aud-truncar">último ficheiro: {progresso.pormenor}</div>
          )}
        </div>
      )}

      {aviso && <p className="aud-aviso">{aviso}</p>}

      <div className="aud-resumo-triagem">
        <span>{c.total_ficheiros} ficheiros no processo</span>
        <span>{c.avaliados} peças avaliadas</span>
        <span>{c.anexos} anexos</span>
        <span>{c.ignorados} ignorados</span>
        {c.ilegiveis > 0 && <span className="aud-sub-alerta">{c.ilegiveis} ilegíveis</span>}
        {c.por_processar > 0 && <span className="aud-sub-alerta">{c.por_processar} por processar</span>}
      </div>

      <SeccaoColapsavel
        titulo="Problemas"
        onAbrir={carregarProblemas}
        badge={modoUnico
          ? <span className="aud-badge aud-modo-unico">parecer único</span>
          : (c.graves > 0
            ? <span className="aud-badge aud-grav-grave">{c.graves} graves</span>
            : null)}
        resumo={modoUnico
          ? 'sem análise por peça'
          : (c.atencao > 0 ? `${c.atencao} atenção · ${c.ok} sem reparos` : `${c.ok} sem reparos`)}
      >
        <ProblemasLista
          problemas={problemas}
          avaliarPecaIndividual={!modoUnico}
          onAbrirPeca={(peca) => setAlvo({
            tipo: 'avaliacao', refId: peca.id, titulo: peca.nome_original
          })}
        />
      </SeccaoColapsavel>

      <SeccaoColapsavel
        titulo="Ficheiros"
        defaultOpen
        onAbrir={carregarFicheiros}
        resumo={`${c.avaliados} avaliações · ${c.anexos} anexos · ${c.ignorados} ignorados · ${c.ilegiveis} ilegíveis`}
      >
        <div className="aud-arvore-accoes">
          <button
            type="button"
            className="aud-btn-secundario"
            disabled={zipEmCurso !== null}
            onClick={() => baixarZip('parecer')}
            title="ZIP com a cronologia, o resumo das avaliações, o parecer final e a pasta avaliacoes/"
          >
            {zipEmCurso === 'parecer' ? 'A PREPARAR…' : '⬇ DOWNLOAD PARECER'}
          </button>
          <button
            type="button"
            className="aud-btn-secundario"
            disabled={zipEmCurso !== null}
            onClick={() => baixarZip('processo')}
            title="ZIP com a cronologia e a pasta transcricoes/ (peças e anexos)"
          >
            {zipEmCurso === 'processo' ? 'A PREPARAR…' : '⬇ DOWNLOAD PROCESSO'}
          </button>
        </div>

        <FicheirosArvore ficheiros={ficheiros} onAbrir={setAlvo} />
      </SeccaoColapsavel>

      <SeccaoColapsavel
        titulo="Histórico"
        resumo={`${auditoria.execucoes.length} execuções`}
      >
        {/* Validar é o aval do escritório sobre a auditoria, não do seu autor */}
        <HistoricoLista
          execucoes={auditoria.execucoes}
          onValidar={admin ? validar : null}
        />
      </SeccaoColapsavel>

      {parecerAberto && (
        <ParecerDialog auditoriaId={id} onClose={() => setParecerAberto(false)} />
      )}

      {alvo && (
        <ConteudoDialog
          auditoriaId={id}
          alvo={alvo}
          onClose={() => setAlvo(null)}
          onAnotar={anotar}
        />
      )}
    </div>
  );
};

export default Detalhe;
